/**
 * The four nutrients beyond the macros: fibre, sugar, salt and saturated fat.
 *
 * Kept apart from `Macros` on purpose, and the separation is the design rather
 * than tidiness:
 *
 * - `Macros` is also the shape of a *target*. There is no daily target for
 *   sugar, so a `remaining.sugar_g` would be a number that means nothing, and
 *   the goals form would grow a field for a budget the server cannot store.
 * - The arithmetic does not carry over. `energySplit` divides energy by Atwater
 *   factors and `KCAL_PER_GRAM` has no entry for sodium; `ringModel` draws an
 *   arc per key.
 * - Every field on `Macros` is a non-null `number` and every function assumes
 *   it. These are `number | null`, and that is the whole feature: a packet that
 *   does not print its saturates has not said it has none.
 *
 * So instead of a total, these produce a total *and how much of the day it
 * could see*. "Sugar 48 g, from 6 of 9 entries" is the honest answer; "48 g"
 * alone claims a completeness the data does not have, and "0 g" for a day of
 * unlabelled home cooking is worse than saying nothing at all.
 *
 * Totals are derived here, from the entries, rather than read off the day — so
 * an entry logged offline moves them immediately, and web and phone cannot
 * disagree about the same day.
 */

export type NutrientKey = "saturated_fat_g" | "fiber_g" | "sugar_g" | "sodium_mg";

/**
 * One nutrient on a food or an entry. Null is "the source never said".
 *
 * `undefined` is accepted everywhere a value is read, and means the same
 * thing. The offline queue persists its optimistic rows to storage and replays
 * them verbatim after a relaunch, so a row written by a build that predates
 * this file arrives with these keys missing entirely.
 */
export type Nutrients = Record<NutrientKey, number | null>;

export const NO_NUTRIENTS: Nutrients = {
  saturated_fat_g: null,
  fiber_g: null,
  sugar_g: null,
  sodium_mg: null,
};

/**
 * Salt in grams from sodium in milligrams.
 *
 * EU labels print salt, in grams, and so does the app — see `NUTRIENTS`. The
 * factor is the same 400 mg of sodium per gram of salt the label reader uses
 * when a panel prints salt and we store sodium, and it lives in exactly one
 * place on each side. Two copies of it is a food wrong by two and a half
 * times, in a direction nobody would spot, on a row other people scan.
 */
export const SODIUM_MG_PER_SALT_G = 400;

export function saltG(sodiumMg: number): number {
  return sodiumMg / SODIUM_MG_PER_SALT_G;
}

export function sodiumMg(salt: number): number {
  return salt * SODIUM_MG_PER_SALT_G;
}

export interface NutrientSpec {
  key: NutrientKey;
  /** The per-100 column on a food, for the same nutrient. */
  per100Key: "saturated_fat_g_per_100" | "fiber_g_per_100" | "sugar_g_per_100" | "sodium_mg_per_100";
  /** British spelling, because the copy is British. The wire is American. */
  label: string;
  /** For the per-meal line, where four labels have to fit one row. */
  shortLabel: string;
  unit: string;
  decimals: number;
  /**
   * Stored value to displayed value.
   *
   * Only salt does anything here. It is stored as sodium in milligrams — the
   * neutral unit, and the one every source supplies — and shown in grams,
   * because that is what is printed on the packet somebody is holding.
   */
  display: (stored: number) => number;
  /** Displayed value back to stored. The inverse of `display`. */
  store: (shown: number) => number;
}

const identity = (value: number) => value;

/**
 * The order, the labels and the precision, in one place.
 *
 * Both clients render from this list, which is what stops the phone and the
 * browser drifting on which nutrient comes first or how many decimals salt
 * gets — the same reason `macros.ts` exists at all.
 *
 * Grams at nought decimals, matching every other gram figure on the day
 * screen. Salt is the exception at one, because the reference intake is about
 * 6 g and whole grams is a scale on which nothing ever visibly moves.
 */
export const NUTRIENTS: readonly NutrientSpec[] = [
  {
    key: "fiber_g",
    per100Key: "fiber_g_per_100",
    label: "Fibre",
    shortLabel: "Fibre",
    unit: "g",
    decimals: 0,
    display: identity,
    store: identity,
  },
  {
    key: "sugar_g",
    per100Key: "sugar_g_per_100",
    label: "Sugar",
    shortLabel: "Sugar",
    unit: "g",
    decimals: 0,
    display: identity,
    store: identity,
  },
  {
    key: "saturated_fat_g",
    per100Key: "saturated_fat_g_per_100",
    label: "Saturated fat",
    shortLabel: "Sat fat",
    unit: "g",
    decimals: 0,
    display: identity,
    store: identity,
  },
  {
    key: "sodium_mg",
    per100Key: "sodium_mg_per_100",
    label: "Salt",
    shortLabel: "Salt",
    unit: "g",
    decimals: 1,
    display: saltG,
    store: sodiumMg,
  },
] as const;

/**
 * A figure, and how much of what it was summed over could supply one.
 *
 * `counted === 0` means nothing could say — which is different from a total of
 * nought, and must never be rendered as one.
 */
export interface NutrientTotal {
  key: NutrientKey;
  total: number;
  counted: number;
  of: number;
}

export type NutrientTotals = Record<NutrientKey, NutrientTotal>;

/**
 * True for a figure a source actually supplied.
 *
 * A typed `0` is data, not absence: a can of diet cola really does have no
 * sugar, and that fact must count towards coverage. So this tests finiteness
 * rather than truthiness, and every `|| 0` and `|| null` in this area is a
 * bug — one turns a gap into a confident nought, the other turns a real nought
 * into a gap.
 */
export function isKnown(value: number | null | undefined): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

/** Sum the four across some entries, counting what each could contribute. */
export function nutrientTotals(entries: readonly Partial<Nutrients>[]): NutrientTotals {
  const totals = {} as NutrientTotals;
  for (const { key } of NUTRIENTS) {
    let total = 0;
    let counted = 0;
    for (const entry of entries) {
      const value = entry?.[key];
      if (isKnown(value)) {
        total += value;
        counted += 1;
      }
    }
    totals[key] = { key, total, counted, of: entries.length };
  }
  return totals;
}

/** The worst coverage across the four, for a one-line summary. */
export function worstCoverage(totals: NutrientTotals): NutrientTotal | null {
  let worst: NutrientTotal | null = null;
  for (const { key } of NUTRIENTS) {
    const row = totals[key];
    if (!worst || row.counted < worst.counted) worst = row;
  }
  return worst;
}

/** Whether anything is missing — the trigger for saying so on screen. */
export function isComplete(totals: NutrientTotals): boolean {
  return NUTRIENTS.every(({ key }) => totals[key].counted === totals[key].of);
}

/** The per-100 shape a food carries, as `forGramsNutrients` wants it. */
export type NutrientsPer100 = Partial<
  Record<NutrientSpec["per100Key"], number | null | undefined>
>;

/**
 * Scale a food's per-100 figures to a portion.
 *
 * Unknown scales to unknown. Without the explicit guard `null * 1.4` is `0` in
 * JavaScript, which fabricates a confident nought out of a gap — silently, and
 * onto a row that is then stored.
 */
export function forGramsNutrients(per100: NutrientsPer100, grams: number): Nutrients {
  const factor = grams / 100;
  const scaled = {} as Nutrients;
  for (const { key, per100Key } of NUTRIENTS) {
    const value = per100[per100Key];
    scaled[key] = isKnown(value) ? value * factor : null;
  }
  return scaled;
}

/** Pull just the four off anything carrying them, normalising undefined to null. */
export function pickNutrients(source: Partial<Nutrients> | null | undefined): Nutrients {
  const picked = {} as Nutrients;
  for (const { key } of NUTRIENTS) {
    const value = source?.[key];
    picked[key] = isKnown(value) ? value : null;
  }
  return picked;
}

/** A figure ready to render: already in display units, already rounded. */
export function formatNutrient(spec: NutrientSpec, total: number): string {
  return spec.display(total).toFixed(spec.decimals);
}

/**
 * A food's figures, in the shape `forGrams` and `forGramsNutrients` take.
 *
 * Both clients built this literal by hand in four places, and the moment a
 * nutrient was added three of them were quietly logging it as unknown. That is
 * the failure worth centralising against: it writes a gap into `food_logs`
 * permanently, on rows a later release cannot go back and repair.
 */
export function perHundred(food: {
  kcal_per_100: number;
  protein_g_per_100: number;
  carbs_g_per_100: number;
  fat_g_per_100: number;
  saturated_fat_g_per_100?: number | null;
  fiber_g_per_100?: number | null;
  sugar_g_per_100?: number | null;
  sodium_mg_per_100?: number | null;
}) {
  return {
    kcal: food.kcal_per_100,
    protein_g: food.protein_g_per_100,
    carbs_g: food.carbs_g_per_100,
    fat_g: food.fat_g_per_100,
    saturated_fat_g_per_100: food.saturated_fat_g_per_100 ?? null,
    fiber_g_per_100: food.fiber_g_per_100 ?? null,
    sugar_g_per_100: food.sugar_g_per_100 ?? null,
    sodium_mg_per_100: food.sodium_mg_per_100 ?? null,
  };
}
